/**
 * FERP Portal - State List Page
 *
 * MONITOR phase primitive - Events (state changes).
 * Displays events from backend via useListEventsQuery.
 * Events represent state transitions in the workflow engine.
 *
 * CONSTRAINTS:
 * - Mantine v8.x only
 * - Data from backend via useListEventsQuery
 * - Multi-tenant via useTenantId()
 * - Dumb Frontend: All data from backend, no hardcoded values
 */

import { useMemo, useState } from 'react';
import { useTerminology } from '@ferp/workflow-forms';
import {
  Container,
  Title,
  Text,
  Card,
  Group,
  Stack,
  Badge,
  Table,
  Paper,
  ThemeIcon,
  Alert,
  Button,
  Loader,
  TextInput,
} from '@mantine/core';
import {
  IconCircleDot,
  IconAlertTriangle,
  IconRefresh,
  IconSearch,
} from '@tabler/icons-react';
import { useTenantId } from '@/auth';
import {
  useListEventsQuery,
  GqlEventSeverity,
} from '@/generated/graphql';

export function StateListPage() {
  const { translate } = useTerminology();
  const tenantId = useTenantId() ?? '';
  const skip = !tenantId;
  const [searchQuery, setSearchQuery] = useState('');

  const { data, loading, error, refetch } = useListEventsQuery({
    variables: { tenantId, limit: 100 },
    skip,
  });

  const events = useMemo(() => {
    return data?.listEvents?.items ?? [];
  }, [data]);

  const filteredEvents = useMemo(() => {
    if (!searchQuery) return events;
    const query = searchQuery.toLowerCase();
    return events.filter((e) =>
      e.eventName.toLowerCase().includes(query) ||
      e.eventType?.toLowerCase().includes(query) ||
      e.entityType?.toLowerCase().includes(query) ||
      e.category?.toLowerCase().includes(query)
    );
  }, [events, searchQuery]);

  const stats = useMemo(() => {
    const total = events.length;
    const critical = events.filter((e) => e.severity === GqlEventSeverity.CRITICAL).length;
    const errors = events.filter((e) => e.severity === GqlEventSeverity.ERROR).length;
    return { total, critical, errors };
  }, [events]);

  const severityColor = (severity: string) => {
    switch (severity) {
      case GqlEventSeverity.CRITICAL: return 'red';
      case GqlEventSeverity.ERROR: return 'orange';
      case GqlEventSeverity.WARNING: return 'yellow';
      case GqlEventSeverity.INFO: return 'blue';
      default: return 'gray';
    }
  };

  if (!tenantId) {
    return (
      <Container size="lg" py="xl">
        <Alert icon={<IconAlertTriangle size={16} />} color="yellow" title="No Tenant Selected">
          Please select a tenant to view state events.
        </Alert>
      </Container>
    );
  }

  if (error) {
    return (
      <Container size="lg" py="xl">
        <Alert icon={<IconAlertTriangle size={16} />} color="red" title="Error Loading Events">
          {error.message}
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between">
          <div>
            <Title order={2}>{translate('event')}s</Title>
            <Text c="dimmed" size="sm">
              {translate('workflow')} state transitions and system {translate('event').toLowerCase()}s
            </Text>
          </div>
          <Button
            leftSection={<IconRefresh size={16} />}
            variant="light"
            onClick={() => refetch()}
            loading={loading}
          >
            Refresh
          </Button>
        </Group>

        {/* Stats Cards */}
        <Group gap="md">
          <Card withBorder p="md" style={{ flex: 1 }}>
            <Group gap="xs">
              <ThemeIcon variant="light" size="sm">
                <IconCircleDot size={14} />
              </ThemeIcon>
              <div>
                <Text size="xs" c="dimmed">Total Events</Text>
                <Text size="xl" fw={700}>{stats.total}</Text>
              </div>
            </Group>
          </Card>
          <Card withBorder p="md" style={{ flex: 1 }}>
            <Group gap="xs">
              <ThemeIcon variant="light" size="sm" color="red">
                <IconAlertTriangle size={14} />
              </ThemeIcon>
              <div>
                <Text size="xs" c="dimmed">Critical</Text>
                <Text size="xl" fw={700}>{stats.critical}</Text>
              </div>
            </Group>
          </Card>
          <Card withBorder p="md" style={{ flex: 1 }}>
            <Group gap="xs">
              <ThemeIcon variant="light" size="sm" color="orange">
                <IconAlertTriangle size={14} />
              </ThemeIcon>
              <div>
                <Text size="xs" c="dimmed">Errors</Text>
                <Text size="xl" fw={700}>{stats.errors}</Text>
              </div>
            </Group>
          </Card>
        </Group>

        {/* Search */}
        <TextInput
          placeholder="Search events..."
          leftSection={<IconSearch size={16} />}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.currentTarget.value)}
        />

        {/* Events Table */}
        {loading ? (
          <Group justify="center" py="xl">
            <Loader />
          </Group>
        ) : (
          <Paper withBorder>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Event</Table.Th>
                  <Table.Th>Type</Table.Th>
                  <Table.Th>Entity</Table.Th>
                  <Table.Th>Severity</Table.Th>
                  <Table.Th>Category</Table.Th>
                  <Table.Th>Performed</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {filteredEvents.length === 0 ? (
                  <Table.Tr>
                    <Table.Td colSpan={6}>
                      <Text ta="center" c="dimmed" py="md">
                        No events found
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ) : (
                  filteredEvents.slice(0, 50).map((event) => (
                    <Table.Tr key={event.id}>
                      <Table.Td>
                        <Text size="sm" fw={500} lineClamp={1}>{event.eventName}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Badge variant="light" size="sm">{event.eventType}</Badge>
                      </Table.Td>
                      <Table.Td>
                        <Stack gap={0}>
                          <Text size="xs" c="dimmed">{event.entityType}</Text>
                        </Stack>
                      </Table.Td>
                      <Table.Td>
                        <Badge color={severityColor(event.severity)} size="sm">
                          {event.severity}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Badge variant="outline" size="sm">{event.category}</Badge>
                      </Table.Td>
                      <Table.Td>
                        <Text size="xs" c="dimmed">
                          {new Date(event.performedAt).toLocaleString()}
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  ))
                )}
              </Table.Tbody>
            </Table>
          </Paper>
        )}

        <Badge variant="light" color="cyan" style={{ alignSelf: 'flex-start' }}>
          MONITOR Phase
        </Badge>
      </Stack>
    </Container>
  );
}

export default StateListPage;
